import { addClass } from '@tyler-components-web/core';
import { tylIconClose } from '@tylertech/tyler-icons/standard';
import { createVisuallyHiddenElement } from '../core';
import { DATEPICKER_CONSTANTS } from '../datepicker/datepicker-constants';
import { DIALOG_CONSTANTS } from '../dialog';
import { ICON_BUTTON_CONSTANTS } from '../icon-button';
import { POPUP_CONSTANTS, PopupPlacement, PopupAnimationType } from '../popup';
import { SCAFFOLD_CONSTANTS } from '../scaffold';
import { TOOLBAR_CONSTANTS } from '../toolbar';
import { CALENDAR_CONSTANTS } from '../calendar/calendar-constants';
import { DATE_RANGE_PICKER_CONSTANTS } from '../date-range-picker/date-range-picker-constants';
import { ICON_CONSTANTS, TylerIconRegistry } from '../icon';
import { BUTTON_CONSTANTS } from '../button';
var CalendarDropdownAdapter = /** @class */ (function () {
    function CalendarDropdownAdapter(_targetElement) {
        this._targetElement = _targetElement;
        TylerIconRegistry.define(tylIconClose);
    }
    CalendarDropdownAdapter.prototype.open = function (config, isMobile, onClose) {
        var _this = this;
        this._calendarElement = this._createCalendar(config, isMobile);
        this._calendarElement.range = config.range;
        this._calendarElement.mode = 'picker';
        this._calendarElement.disableDayCallback = config.disableDayCallback;
        this._announcerElement = createVisuallyHiddenElement();
        this._announcerElement.id = config.id + "-activedescendant";
        // Determine if we need to display the calendar in a dialog or popup
        if (isMobile) {
            this._dropdownElement = this._createDialog(config);
            this._dropdownElement.fullscreen = true;
            this._dropdownElement.addEventListener('mousedown', function (evt) { return evt.preventDefault(); });
            var dialogContent = this._buildDialogContent(config, this._calendarElement, onClose);
            this._dropdownElement.appendChild(dialogContent);
            this._dropdownElement.appendChild(this._announcerElement);
        }
        else {
            this._dropdownElement = this._createDropdown(config);
            var dropdownContent = this._createDropdownContent(config, this._calendarElement);
            this._dropdownElement.appendChild(dropdownContent);
            this._dropdownElement.appendChild(this._announcerElement);
            this._dropdownElement.static = true;
        }
        // Add a listener for when the activedescendant is updated
        this._calendarElement.addEventListener(CALENDAR_CONSTANTS.events.ACTIVE_CHANGE, function (evt) {
            var id = config.id + "-activedescendant-";
            switch (evt.detail.type) {
                case 'date':
                    id += "date-day-" + evt.detail.rowIndex + "-" + evt.detail.colIndex;
                    break;
                case 'month':
                    id += "month-" + evt.detail.month;
                    break;
                case 'year':
                    id += "year-" + evt.detail.year;
                    break;
            }
            config.activeChangeCallback(id);
            _this._announcerElement.id = id;
            _this._announcerElement.setAttribute('aria-selected', evt.detail.selected.toString());
            _this._announcerElement.textContent = evt.detail.text;
        });
        // Open the popup
        this._dropdownElement.open = true;
        // Attempt to re-layout the calendar to account for updates in rendering during scale
        // transition within popup (fixes ripple alignment... etc.)
        window.requestAnimationFrame(function () {
            setTimeout(function () {
                if (_this._dropdownElement && _this._dropdownElement.open && _this._calendarElement && _this._calendarElement.isConnected) {
                    _this._calendarElement.layout();
                }
            }, POPUP_CONSTANTS.numbers.ANIMATION_DURATION);
        });
    };
    CalendarDropdownAdapter.prototype.close = function () {
        if (!this._dropdownElement) {
            return;
        }
        this._dropdownElement.open = false;
        this._dropdownElement = undefined;
        this._calendarElement = undefined;
    };
    CalendarDropdownAdapter.prototype.setSelectListener = function (listener, range) {
        if (this._calendarElement) {
            if (range) {
                this._calendarElement.addEventListener(CALENDAR_CONSTANTS.events.DATE_RANGE_SELECTED, listener);
            }
            else {
                this._calendarElement.addEventListener(CALENDAR_CONSTANTS.events.DATE_SELECTED, listener);
            }
        }
    };
    CalendarDropdownAdapter.prototype.propagateKey = function (key, isShiftKey) {
        if (this._calendarElement) {
            this._calendarElement.handleKey(key, isShiftKey);
        }
    };
    CalendarDropdownAdapter.prototype.setValue = function (value) {
        if (this._calendarElement) {
            this._calendarElement.value = value;
        }
    };
    CalendarDropdownAdapter.prototype.setMinDate = function (value) {
        if (this._calendarElement) {
            this._calendarElement.minDate = value;
        }
    };
    CalendarDropdownAdapter.prototype.setMaxDate = function (value) {
        if (this._calendarElement) {
            this._calendarElement.maxDate = value;
        }
    };
    CalendarDropdownAdapter.prototype.setBlockedDates = function (value) {
        if (this._calendarElement) {
            this._calendarElement.blockedDates = value;
        }
    };
    CalendarDropdownAdapter.prototype.getScreenWidth = function () {
        return window.innerWidth;
    };
    CalendarDropdownAdapter.prototype.setActiveDate = function (date) {
        if (this._calendarElement) {
            this._calendarElement.setActiveDate(date);
        }
    };
    CalendarDropdownAdapter.prototype.setDisabledDaysOfWeek = function (value) {
        if (this._calendarElement) {
            this._calendarElement.disabledDaysOfWeek = value;
        }
    };
    CalendarDropdownAdapter.prototype.setDisableDayCallback = function (value) {
        if (this._calendarElement) {
            this._calendarElement.disableDayCallback = value;
        }
    };
    CalendarDropdownAdapter.prototype._createDropdown = function (config) {
        var popupElement = document.createElement(POPUP_CONSTANTS.elementName);
        popupElement.targetElement = this._targetElement;
        popupElement.placement = PopupPlacement.BottomLeft;
        popupElement.animationType = PopupAnimationType.Menu;
        popupElement.classList.add(config.range ? DATE_RANGE_PICKER_CONSTANTS.classes.POPUP : DATEPICKER_CONSTANTS.classes.POPUP);
        if (config.id) {
            popupElement.id = config.id;
        }
        if (config.popupClasses) {
            addClass(config.popupClasses, popupElement);
        }
        return popupElement;
    };
    CalendarDropdownAdapter.prototype._createDropdownContent = function (config, calendarElement) {
        var container = document.createElement('div');
        container.appendChild(calendarElement);
        if (config.showToday || config.showClear) {
            var footerElement = this._createFooterToolbar(config);
            container.appendChild(footerElement);
        }
        return container;
    };
    CalendarDropdownAdapter.prototype._createFooterToolbar = function (config) {
        var footerToolbarElement = document.createElement(TOOLBAR_CONSTANTS.elementName);
        footerToolbarElement.inverted = true;
        if (config.showToday) {
            var todayButton = document.createElement(BUTTON_CONSTANTS.elementName);
            todayButton.slot = 'start';
            var todayButtonElement = document.createElement('button');
            todayButtonElement.type = 'button';
            todayButtonElement.textContent = 'Today';
            todayButtonElement.id = 'today-button';
            todayButtonElement.addEventListener('mousedown', function (evt) {
                evt.stopPropagation();
                evt.preventDefault();
                if (typeof config.todayCallback === 'function') {
                    config.todayCallback();
                }
            });
            todayButton.appendChild(todayButtonElement);
            footerToolbarElement.appendChild(todayButton);
        }
        if (config.showClear) {
            var clearButton = document.createElement(BUTTON_CONSTANTS.elementName);
            clearButton.slot = 'end';
            var clearButtonElement = document.createElement('button');
            clearButtonElement.type = 'button';
            clearButtonElement.textContent = 'Clear';
            clearButtonElement.id = 'clear-button';
            clearButtonElement.addEventListener('mousedown', function (evt) {
                evt.stopPropagation();
                evt.preventDefault();
                if (typeof config.clearCallback === 'function') {
                    config.clearCallback();
                }
            });
            clearButton.appendChild(clearButtonElement);
            footerToolbarElement.appendChild(clearButton);
        }
        return footerToolbarElement;
    };
    CalendarDropdownAdapter.prototype._createDialog = function (config) {
        var dialogElement = document.createElement(DIALOG_CONSTANTS.elementName);
        dialogElement.classList.add(config.range ? DATE_RANGE_PICKER_CONSTANTS.classes.POPUP : DATEPICKER_CONSTANTS.classes.POPUP);
        if (config.id) {
            dialogElement.id = config.id;
        }
        if (config.popupClasses) {
            addClass(config.popupClasses, dialogElement);
        }
        return dialogElement;
    };
    CalendarDropdownAdapter.prototype._createCalendar = function (config, isMobile) {
        var calendarElement = document.createElement(CALENDAR_CONSTANTS.elementName);
        calendarElement.range = config.range;
        calendarElement.value = config.value;
        calendarElement.minDate = config.minDate;
        calendarElement.maxDate = config.maxDate;
        calendarElement.blockedDates = config.blockedDates;
        calendarElement.autoSwitch = true;
        calendarElement.preventFocus = true;
        calendarElement.disabledDaysOfWeek = config.disabledDaysOfWeek;
        calendarElement.viewIndex = config.startView;
        if (!isMobile) {
            calendarElement.setAttribute('context', 'dropdown');
        }
        return calendarElement;
    };
    CalendarDropdownAdapter.prototype._buildDialogContent = function (config, calendarElement, closeCb) {
        var scaffold = document.createElement(SCAFFOLD_CONSTANTS.elementName);
        var container = document.createElement('div');
        container.style.display = 'flex';
        container.style.marginTop = '16px';
        container.style.justifyContent = 'center';
        container.slot = 'body';
        container.appendChild(calendarElement);
        scaffold.appendChild(container);
        var headerContainer = document.createElement(TOOLBAR_CONSTANTS.elementName);
        headerContainer.slot = 'header';
        scaffold.appendChild(headerContainer);
        var header = document.createElement('h1');
        header.slot = 'start';
        header.style.fontSize = '1.25rem';
        header.style.lineHeight = '2rem';
        header.style.fontWeight = '400';
        header.style.letterSpacing = '0.0125em';
        header.classList.add('tyl-typography--title');
        header.textContent = 'Choose a date';
        headerContainer.appendChild(header);
        var closeIconButton = document.createElement(ICON_BUTTON_CONSTANTS.elementName);
        closeIconButton.slot = 'end';
        headerContainer.appendChild(closeIconButton);
        var button = document.createElement('button');
        button.setAttribute('aria-label', 'Close the datepicker');
        button.addEventListener('click', function () {
            if (typeof closeCb === 'function') {
                closeCb();
            }
        });
        var iconElement = document.createElement(ICON_CONSTANTS.elementName);
        iconElement.name = 'close';
        button.appendChild(iconElement);
        closeIconButton.appendChild(button);
        if (config.showToday || config.showClear) {
            var footerElement = this._createFooterToolbar(config);
            footerElement.slot = 'footer';
            scaffold.appendChild(footerElement);
        }
        return scaffold;
    };
    return CalendarDropdownAdapter;
}());
export { CalendarDropdownAdapter };
